import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../lib/api';
import { formatDate, generateReferralLink, getRankLabel } from '../../lib/utils';
import { GlassCard, StatusBadge, RankBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Phone, CreditCard, Building, Copy, Check, Shield } from 'lucide-react';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [kycFile, setKycFile] = useState(null);

  const handleUploadKYC = async () => {
    if (!kycFile) { toast.error('Please select a file'); return; }
    setLoading(true);
    try {
      await authAPI.uploadKYC(kycFile);
      toast.success('KYC document uploaded. Pending approval.');
      const res = await authAPI.getMe();
      updateUser(res.data);
      setKycFile(null);
    } catch (error) { toast.error('Failed to upload KYC document'); }
    finally { setLoading(false); }
  };

  const copyReferralLink = () => {
    const link = generateReferralLink(user?.referral_code);
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const InfoField = ({ label, icon: Icon, children }) => (
    <div>
      <Label className="text-gray-500">{label}</Label>
      <div className="flex items-center gap-2 mt-1 p-3 rounded-lg bg-gray-50 border border-gray-100">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-gray-900">{children}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-500 mt-1">Manage your account settings</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <GlassCard>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <InfoField label="Full Name" icon={User}>{user?.full_name}</InfoField>
              <InfoField label="Email" icon={Mail}>{user?.email}</InfoField>
              <InfoField label="Phone" icon={Phone}>{user?.phone}</InfoField>
              <InfoField label="IC Number" icon={CreditCard}>{user?.ic_number || '-'}</InfoField>
              <InfoField label="Role"><span className="capitalize">{user?.role}</span></InfoField>
              <InfoField label="Member Since">{formatDate(user?.created_at)}</InfoField>
            </div>
          </GlassCard>

          <GlassCard>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <InfoField label="Bank Name" icon={Building}>{user?.bank_name || '-'}</InfoField>
              <InfoField label="Account Number">{user?.bank_account || '-'}</InfoField>
              <div className="md:col-span-2">
                <InfoField label="Account Holder Name">{user?.bank_holder_name || '-'}</InfoField>
              </div>
            </div>
          </GlassCard>

          {user?.role === 'agent' && (
            <GlassCard>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Agent Code</Label>
                  <div className="flex items-center gap-2 mt-1 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <span className="text-[#4E50FF] font-mono font-semibold">{user?.agent_code}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Rank</Label>
                  <div className="flex items-center gap-2 mt-1 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <RankBadge rank={user?.agent_rank} />
                    <span className="text-gray-900">{getRankLabel(user?.agent_rank)}</span>
                  </div>
                </div>
              </div>
            </GlassCard>
          )}
        </div>

        <div className="space-y-6">
          <GlassCard>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-[#4E50FF]" />
              <h2 className="text-lg font-semibold text-gray-900">KYC Verification</h2>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-500">Status</span>
              <StatusBadge status={user?.kyc_status} />
            </div>

            {user?.kyc_status === 'pending' && !user?.kyc_document && (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">Upload your IC (front) to verify your identity</p>
                <Input type="file" accept="image/*,.pdf" onChange={(e) => setKycFile(e.target.files[0])} data-testid="kyc-upload" />
                <Button onClick={handleUploadKYC} disabled={loading || !kycFile} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="kyc-submit">
                  {loading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </div>
            )}
            {user?.kyc_status === 'pending' && user?.kyc_document && (
              <p className="text-sm text-amber-600">Your document is under review. This usually takes 1-2 business days.</p>
            )}
            {user?.kyc_status === 'approved' && (
              <div className="flex items-center gap-2 text-emerald-600"><Check className="w-5 h-5" /><span>Verified</span></div>
            )}
            {user?.kyc_status === 'rejected' && (
              <div className="space-y-3">
                <p className="text-sm text-red-500">Your KYC was rejected. Please upload a clearer document.</p>
                <Input type="file" accept="image/*,.pdf" onChange={(e) => setKycFile(e.target.files[0])} />
                <Button onClick={handleUploadKYC} disabled={loading || !kycFile} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white">
                  {loading ? 'Uploading...' : 'Re-upload Document'}
                </Button>
              </div>
            )}
          </GlassCard>

          <GlassCard className="border-l-4 border-l-[#4E50FF]">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Code</h2>
            <div className="p-4 rounded-lg bg-[#4E50FF]/5 border border-[#4E50FF]/20 text-center mb-4">
              <span className="text-2xl font-mono font-bold text-[#4E50FF]">{user?.referral_code}</span>
            </div>
            <Button onClick={copyReferralLink} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="copy-referral">
              <Copy className="w-4 h-4 mr-2" />Copy Referral Link
            </Button>
            <p className="text-xs text-gray-400 text-center mt-3">Share this link and earn commission on referral purchases</p>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default Profile;
